import { createStitches } from '@stitches/react'
import { FilterUnion } from 'types/FilterUnion'

const media = {
  mobile: '(min-width: 320px)',
  bp1: '(min-width: 520px)',
  bp1_1: '(min-width: 768px)',
  bp2: '(min-width: 900px)',
  bp2_1: '(min-width: 1024px)',
  bp3: '(min-width: 1200px)',
  bp3_1: '(min-width: 1420px)',
  bp4: '(min-width: 1800px)',
  bpInt: '(max-width: 767px)',
  bppsd: '(min-width: 1349px) and (max-width: 1350px)',
  bppsm:
    '(min-width: 411px) and (max-width: 412px) and (min-height: 822px) and (max-height: 823px)',
  motion: '(prefers-reduced-motion)',
  hover: '(any-hover: hover)',
  dark: '(prefers-color-scheme: dark)',
  light: '(prefers-color-scheme: light)',
} as const

export const {
  styled,
  css,
  getCssText,
  config,
  globalCss: stitchesGlobal,
  theme,
  keyframes,
} = createStitches({
  theme: {
    colors: {
      'primary-400': '#EB4422',
      'primary-500': '#D73D1F',
      'primary-600': '#C2371C',
      'primary-700': '#871C20',
      'primary-800': '#881C20',
      'secondary-300': '#5087C3',
      'secondary-300-20': 'rgba(80, 135, 195, 0.20)',
      'secondary-400': '#1855A0',
      'secondary-500': '#114680',
      'secondary-600': '#0D3866',
      'tertiary-50': '#FAFAFA',
      'tertiary-100': '#EEEEEE',
      'tertiary-150': '#E5E5E5',
      'tertiary-200': '#FFFAF2',
      'tertiary-250': '#FDF8EF',
      'tertiary-300': '#DCD4BE',
      'tertiary-400': '#8D8D8D',
      'tertiary-450': '#999999',
      'tertiary-800': '#333333',
      'tertiary-1000': '#5B5B5B',
      'tertiary-1050': '#525252',
      'tertiary-1075': '#171717',
      'tertiary-1100': '#1A1A1A',
      'quaternary-100': '#FFC9B2', // Quaternary colors currently being used for recipe pages. Will be sunset soon.
      'quaternary-110': '#FFB99A',
      'quaternary-200': '#FFCB76',
      'quaternary-210': '#F5A92D',
      'quaternary-220': '#E5C07F',
      'quaternary-300': '#E0B39F',
      'quaternary-310': '#57100B',
      'quaternary-320': '#D5A28E',
      'quaternary-400': '#D5D5D5',
      'quaternary-410': '#C7C7C7',
      'quaternary-500': '#E6B180',
      'quaternary-510': '#C96732',
      'quaternary-520': '#DFA06F',
      'quaternary-600': '#CBC1B8',
      'quaternary-610': '#8E7E71',
      'quaternary-620': '#BEB3A9',
      'success-100': '#EFFFF4',
      'success-500': '#05C574',
      'info-100': '#FFFAF2',
      'error-100': '#FFFAF2',
      'accent-white': '#FFFFFF',
      'accent-black': '#000000',
      'accent-red': '#D73D1F',
      'accent-gray': '#AAAAAA',
      'accent-dark-gray': '#707070',
      'tag-green': '5, 197, 116',
      'tag-green-color': 'rgb($tag-green)',
      'tag-blue': '37, 140, 236',
      'tag-blue-color': 'rgb($tag-blue)',
      'tag-yellow': '245, 169, 45',
      'tag-yellow-color': 'rgb($tag-yellow)',
      'tag-yellow-hover': '255, 189, 65',
      'tag-yellow-hover-color': 'rgb($tag-yellow-hover)',
      'tag-orange': '255, 153, 0',
      'tag-orange-color': 'rgb($tag-orange)',
      'tag-dark-orange': '201, 103, 50',
      'tag-dark-orange-color': 'rgb($tag-dark-orange)',
      'tag-dark-orange-hover': '217, 119, 66',
      'tag-dark-orange-hover-color': 'rgb($tag-dark-orange-hover)',
      'tag-red': '204, 42, 42',
      'tag-red-color': 'rgb($tag-red)',
      transparent: 'transparent',
    },
    space: {
      1: '8px',
      2: '16px',
      3: '24px',
      4: '32px',
      5: '40px',
      6: '48px',
      7: '56px',
      8: '64px',
      9: '72px',
      10: '80px',
      11: '88px',
      12: '96px',
      13: '104px',
      14: '112px',
      15: '120px',
    },
    fontSizes: {
      h1: '52px',
      h2: '44px',
      h3: '34px',
      h4: '28px',
      h5: '24px',
      headline: '20px',
      bodyExtraLarge: '22px',
      bodyLarge: '18px',
      body: '16px',
      small: '14px',
      caption: '12px',
      tiny: '10px',
    },
    lineHeights: {
      h1: 1.192,
      h2: 1.22,
      h3: 1.31,
      h4: 1.42,
      h5: 1.25,
      headline: 1.5,
      body: '24px',
      small: 1.57,
      caption: 1.5,
    },
    fonts: {
      display: 'Beirut Display, serif',
      label: 'Spezia Condensed, sans-serif',
      primary: 'Spezia Wide, sans-serif',
    },
    fontWeights: {
      ultraLight: 100,
      extraLight: 200,
      light: 300,
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
      extraBold: 800,
      black: 900,
    },
    letterSpacings: {
      h1: '-1.3px',
      h2: '-1.1px',
      h3: '-0.8px',
      h4: '-0.7px',
      h5: '-0.6px',
      body: '2%',
      caption: '0.24px',
    },
    sizes: {
      1: '8px',
      2: '16px',
      3: '24px',
      4: '32px',
      5: '40px',
      6: '48px',
      7: '56px',
      8: '64px',
      9: '72px',
      10: '80px',
      11: '88px',
      12: '96px',
      13: '104px',
      14: '112px',
      15: '120px',
      max: '1440px',
    },
    borderWidths: {},
    borderStyles: {},
    radii: {
      1: '4px',
      2: '6px',
      3: '8px',
      4: '12px',
      6: '20px',
      round: '50%',
      pill: '9999px',
    },
    shadows: {},
    zIndices: {
      1: 100,
      2: 200,
      3: 300,
      4: 400,
      max: 999,
    },
    transitions: {},
  },
  media,
  utils: {
    size: (value: any) => ({
      width: value,
      height: value,
    }),
    px: (value: any) => ({
      paddingLeft: value,
      paddingRight: value,
    }),
    py: (value: any) => ({
      paddingTop: value,
      paddingBottom: value,
    }),
    mx: (value: any) => ({
      marginLeft: value,
      marginRight: value,
    }),
    my: (value: any) => ({
      marginTop: value,
      marginBottom: value,
    }),
  },
})

export const globalStyles = stitchesGlobal({
  '*': {
    boxSizing: 'border-box',
    textDecoration: 'none',
    listStyleType: 'none',
    color: 'inherit',
    padding: 0,
    margin: 0,
    scrollBehavior: 'smooth',
  },
  body: {
    fontFamily: '$primary',
    fontWeight: '$medium',
  },
  h1: {
    fontFamily: '$display',
    fontWeight: '$light',
    letterSpacing: '$h1',
  },
  h2: {
    fontFamily: '$display',
    fontWeight: '$light',
    letterSpacing: '$h2',
  },
  h3: {
    fontFamily: '$display',
    fontWeight: '$light',
    letterSpacing: '$h3',
  },
  '@font-face': [
    {
      fontFamily: 'Beirut Display',
      fontWeight: 300,
      src: "url('/fonts/BeirutDisplay-Light.woff2') format('woff2'), url('/fonts/BeirutDisplay-Light.woff') format('woff')",
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Beirut Display',
      fontWeight: 300,
      src: "url('/fonts/BeirutDisplay-LightItalic.woff2') format('woff2'), url('/fonts/BeirutDisplay-LightItalic.woff') format('woff')",
      fontStyle: 'italic',
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Wide',
      fontWeight: 500,
      src: "url('/fonts/SpeziaWide-Medium.woff2') format('woff2'), url('/fonts/SpeziaWide-Medium.woff') format('woff')",
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Wide',
      fontWeight: 500,
      src: "url('/fonts/SpeziaWide-MediumItalic.woff2') format('woff2'), url('/fonts/SpeziaWide-MediumItalic.woff') format('woff')",
      fontStyle: 'italic',
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Wide',
      fontWeight: 600,
      src: "url('/fonts/SpeziaWide-SemiBold.woff2') format('woff2'), url('/fonts/SpeziaWide-SemiBold.woff') format('woff')",
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Wide',
      fontWeight: 600,
      src: "url('/fonts/SpeziaWide-SemiBoldItalic.woff2') format('woff2'), url('/fonts/SpeziaWide-SemiBoldItalic.woff') format('woff')",
      fontStyle: 'italic',
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Condensed',
      fontWeight: 500,
      src: "url('/fonts/SpeziaCondensed-Medium.woff2')",
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Condensed',
      fontWeight: 600,
      src: "url('/fonts/SpeziaCondensed-SemiBold.woff2')",
      fontDisplay: 'swap',
    },
    {
      fontFamily: 'Spezia Condensed',
      fontWeight: 700,
      src: "url('/fonts/SpeziaCondensed-Bold.woff2')",
      fontDisplay: 'swap',
    },
  ],
  '.hide-gorgias-on-mobile #gorgias-chat-container': {
    display: 'none',
    '@bp2': {
      display: 'initial',
    },
  },
  '.hide-accessibility-on-mobile #ae_app': {
    display: 'none !important',
    '@bp2': {
      display: 'initial',
    },
  },
  '.move-accessibility-on-mobile #ae_launcher': {
    bottom: '75px !important',
    '@bp2': {
      bottom: '24px !important',
    },
  },
})

export type ColorTokens = keyof typeof theme.colors
export type SizeTokens = keyof typeof theme.sizes
export type SpaceTokens = keyof typeof theme.space
export type MediaTokens = keyof typeof media

export type FlattenVariant<A> = FilterUnion<A, string>
